import React from "react";
import { useTranslation } from "react-i18next";
import berainLogo from "../../../assets/main-logo.png";
import { formatDate } from "../../../utility";
import "../../../style.css";

type PrintHeaderInfo = {
  employeeId?: string;
  employeeName?: string;
  salesOfficeDisplay?: string;
  printedAt?: string;
  printedBy?: string;
};

type ComponentToPrintProps = {
  data: any;
  startDate?: string;
  endDate?: string;
  headerInfo?: PrintHeaderInfo;
};

const formatAreaStreet = (item: { area?: string; street?: string }) => {
  if (item.area && item.street) return `${item.area} / ${item.street}`;
  return item.area ?? item.street ?? "-";
};

const formatCollectionAmount = (value: any) => {
  if (value == null || value === "") return "-";
  const num = Number(value);
  if (isNaN(num) || num < 0) return "0.00";
  return num.toFixed(2);
};

const formatQuantity = (value: any) => {
  if (value == null || value === "" || value === "-") return "-";
  const num = Number(value);
  if (isNaN(num)) return value;
  return Math.round(num);
};

const convertDateToDDMMYYYY = (dateString?: string) => {
  if (!dateString) return "-";
  const [year, month, day] = dateString.split("-");
  return `${day}-${month}-${year}`;
};

const getAbsoluteLogoUrl = (logoPath: string) => {
  if (/^https?:\/\//i.test(logoPath)) {
    return logoPath;
  }
  const normalizedPath = logoPath.startsWith("/") ? logoPath : `/${logoPath}`;
  return `${window.location.origin}${normalizedPath}`;
};

const ComponentToPrint: React.FC<ComponentToPrintProps> = ({
  data,
  startDate,
  endDate,
  headerInfo,
}) => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const skuSummary = data?.sku_summary;
  const skuSummaryPlanned = data?.sku_summary_planned;
  const skuSummaryUnplanned = data?.sku_summary_unplanned;

  const combinedSummary = React.useMemo(() => {
    const map: Record<string, {
      material_id: string;
      material_description_en: string;
      material_description_ar?: string;
      planned_qty: number;
      unplanned_qty: number;
      total_qty: number;
      uom?: string;
    }> = {};

    const processList = (list: any[], type: "planned" | "unplanned" | "total") => {
      if (!Array.isArray(list)) return;
      list.forEach((item) => {
        const id = item?.material_id;
        if (!id) return;
        const idStr = String(id);
        if (!map[idStr]) {
          map[idStr] = {
            material_id: item.material_id,
            material_description_en: item.material_description_en || "",
            material_description_ar: item.material_description_ar || "",
            planned_qty: 0,
            unplanned_qty: 0,
            total_qty: 0,
            uom: item.uom_en || item.uom || item.UoMText || item.UnitText || item.Uom || item.uom_name || "",
          };
        }
        const qty = Number(item.total_carton_quantity || 0);
        if (type === "planned") {
          map[idStr].planned_qty += qty;
        } else if (type === "unplanned") {
          map[idStr].unplanned_qty += qty;
        } else if (type === "total") {
          map[idStr].total_qty = qty;
        }

        if (!map[idStr].material_description_en && item.material_description_en) {
          map[idStr].material_description_en = item.material_description_en;
        }
        if (!map[idStr].material_description_ar && item.material_description_ar) {
          map[idStr].material_description_ar = item.material_description_ar;
        }
        if (!map[idStr].uom) {
          map[idStr].uom = item.uom_en || item.uom || item.UoMText || item.UnitText || item.Uom || item.uom_name || "";
        }
      });
    };

    processList(skuSummaryPlanned, "planned");
    processList(skuSummaryUnplanned, "unplanned");

    if (skuSummary && skuSummary.length > 0) {
      processList(skuSummary, "total");
    }

    Object.values(map).forEach((item) => {
      if (item.total_qty === 0) {
        item.total_qty = item.planned_qty + item.unplanned_qty;
      }
    });

    return Object.values(map);
  }, [skuSummary, skuSummaryPlanned, skuSummaryUnplanned]);

  return (
    <div id="print-content" className="hidden print:block">
      <style>{`
        #print-content table,
        #print-content th,
        #print-content td {
          border: 1px solid #4b5563 !important;
        }
        #print-content table {
          border-collapse: collapse !important;
        }
      `}</style>
      <div className="mb-4">
        {/* Top Header: Title centered & Logo on top right */}
        <div className="flex justify-between items-center mb-3">
          <div className="w-1/4"></div>
          <div className="w-2/4 text-center">
            <h1 className="text-xl font-bold">{t("planningReport.printTitle")}</h1>
          </div>
          <div className="w-1/4 flex justify-end">
            <img src={berainLogo} alt="Berain Logo" className="h-10 object-contain" />
          </div>
        </div>

        {/* Info Row: Left (Employee Details) and Right (Date & Printed Info) perfectly aligned */}
        <div className="flex justify-between items-start text-xs">
          {/* Left: Employee Info */}
          <div className="flex flex-col gap-0.5 text-left">
            <p className="whitespace-nowrap">
              <span className="font-semibold">{t("planningReportTableFields.employee_id")}:</span>{" "}
              {headerInfo?.employeeId ?? "-"}
            </p>
            <p className="whitespace-nowrap">
              <span className="font-semibold">{t("planningReportTableFields.employee_name")}:</span>{" "}
              {headerInfo?.employeeName ?? "-"}
            </p>
            <p className="whitespace-nowrap">
              <span className="font-semibold">{t("planningReportTableFields.sales_office")}:</span>{" "}
              {headerInfo?.salesOfficeDisplay ?? "-"}
            </p>
          </div>

          {/* Right: Date / Printed Info */}
          <div className="flex flex-col items-end text-right gap-0.5">
            {startDate === endDate ? (
              <p className="whitespace-nowrap">
                <span className="font-semibold">Date:</span> {convertDateToDDMMYYYY(startDate)}
              </p>
            ) : (
              <p className="whitespace-nowrap">
                <span className="font-semibold">Date:</span> {convertDateToDDMMYYYY(startDate)} to{" "}
                {convertDateToDDMMYYYY(endDate)}
              </p>
            )}
            <p className="whitespace-nowrap">
              <span className="font-semibold">{t("planningReport.printedAt")}:</span>{" "}
              {headerInfo?.printedAt ?? "-"}
            </p>
            <p className="whitespace-nowrap">
              <span className="font-semibold">{t("planningReport.printedBy")}:</span>{" "}
              {headerInfo?.printedBy ?? "-"}
            </p>
          </div>
        </div>
      </div>

      <table className="w-full mb-8 border-collapse border border-gray-300 table-fixed" style={{ fontSize: "8px" }}>
        <thead>
          <tr>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "4%" }}>
              {t("planningReportTableFields.sequence_number")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "7%" }}>
              {t("planningReportTableFields.customer_id")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "7%" }}>
              {t("planningReportTableFields.delivery_number")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "7%" }}>
              {t("planningReportTableFields.delivery_date")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "7%" }}>
              {t("planningReportTableFields.time_slot")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "6%" }}>
              {t("planningReportTableFields.delivery_type")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "7%" }}>
              {t("planningReportTableFields.collection_amount")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "5%" }}>
              {t("planningReportTableFields.carton_quantity")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "4%" }}>
              {t("planningReportTableFields.free_carton_quantity")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "5%" }}>
              {t("planningReportTableFields.total_quantity")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "5%" }}>
              {t("planningReportTableFields.total_carton_quantity")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "10%" }}>
              {t("planningReportTableFields.area_street")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "14%" }}>
              {t("planningReportTableFields.customer_name")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "12%" }}>
              {t("planningReportTableFields.customer_mobile_number")}
            </th>
          </tr>
        </thead>
        <tbody>
          {data?.items?.map((item: any, index: number) => (
            <tr key={item.delivery_number ?? `${item.emp_id}-${index}`}>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{item.sequence_number ?? "-"}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{item.customer_id ?? "-"}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{item.delivery_number ?? "-"}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">
                {item.delivery_date ? formatDate(item.delivery_date) : "-"}
              </td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{item.time_slot ?? "-"}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{item.delivery_visit_type ?? "-"}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{formatCollectionAmount(item.amount_to_be_paid)}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{formatQuantity(item.carton_quantity)}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{formatQuantity(item.free_carton_quantity)}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{formatQuantity(item.total_unit_quantity ?? item.total_quantity ?? ((Number(item.carton_quantity) || 0) + (Number(item.free_carton_quantity) || 0)))}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{formatQuantity(item.total_carton_quantity ?? item.total_carton_qty ?? "-")}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{formatAreaStreet(item)}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{item.customer_name ?? "-"}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{item.customer_mobile ?? "-"}</td>
            </tr>
          ))}
        </tbody>
        {data?.items?.length > 0 && (
          <tfoot className="font-bold bg-gray-100">
            <tr>
              <td colSpan={9} className="border border-gray-300 p-1 text-right font-bold">
                {t("planningReportTableFields.grand_total")}:
              </td>
              <td className="border border-gray-300 p-1 font-bold break-words whitespace-normal">
                {formatQuantity(
                  data?.grand_total_carton_quantity ??
                  data?.items?.reduce((acc: number, item: any) => {
                    const qty = item.total_carton_quantity ?? item.total_carton_qty;
                    return acc + (Number(qty) || 0);
                  }, 0)
                )}
              </td>
              <td className="border border-gray-300 p-1 font-bold break-words whitespace-normal">
                {formatQuantity(
                  data?.grand_total_unit_quantity ??
                  data?.items?.reduce((acc: number, item: any) => {
                    const qty =
                      item.total_unit_quantity ??
                      item.total_quantity ??
                      (Number(item.carton_quantity) || 0) +
                      (Number(item.free_carton_quantity) || 0);
                    return acc + (Number(qty) || 0);
                  }, 0)
                )}
              </td>
              <td colSpan={3} className="border border-gray-300 p-1"></td>
            </tr>
          </tfoot>
        )}
      </table>

      {combinedSummary && combinedSummary.length > 0 && (
        <div className="mt-8 mb-8 page-break-inside-avoid">
          <h2 className="text-base font-bold mb-2">
            {t("planningReport.warehouseLoadingSummaryTitle")}
          </h2>
          <table className="w-full mb-6 border-collapse border border-gray-300" style={{ fontSize: "8px" }}>
            <thead>
              <tr>
                <th className="border border-gray-300 p-1.5 bg-gray-100 text-left">
                  {t("planningReport.material")}
                </th>
                <th className="border border-gray-300 p-1.5 bg-gray-100 text-left">
                  {t("planningReport.description")}
                </th>
                <th className="border border-gray-300 p-1.5 bg-gray-100 text-right">
                  {t("planningReport.plannedQty")}
                </th>
                <th className="border border-gray-300 p-1.5 bg-gray-100 text-right">
                  {t("planningReport.unplannedQty")}
                </th>
                <th className="border border-gray-300 p-1.5 bg-gray-100 text-right">
                  {t("planningReport.totalQty")}
                </th>
                <th className="border border-gray-300 p-1.5 bg-gray-100 text-left">
                  {t("planningReport.uom")}
                </th>
              </tr>
            </thead>
            <tbody>
              {combinedSummary.map((item: any, idx: number) => {
                const description = isArabic
                  ? item.material_description_ar || item.material_description_en
                  : item.material_description_en || item.material_description_ar;
                const uomVal = isArabic
                  ? item.uom_ar || item.uom || item.UoMTextAr || item.uom_en || item.UoMText || item.UnitText || item.Uom || item.uom_name
                  : item.uom_en || item.uom || item.UoMText || item.UnitText || item.Uom || item.uom_name || item.uom_ar || item.UoMTextAr;
                return (
                  <tr key={item.material_id ?? idx}>
                    <td className="border border-gray-300 p-1.5 text-left">{item.material_id ?? "-"}</td>
                    <td className="border border-gray-300 p-1.5 text-left">{description ?? "-"}</td>
                    <td className="border border-gray-300 p-1.5 text-right">{item.planned_qty > 0 ? formatQuantity(item.planned_qty) : ""}</td>
                    <td className="border border-gray-300 p-1.5 text-right">{item.unplanned_qty > 0 ? formatQuantity(item.unplanned_qty) : ""}</td>
                    <td className="border border-gray-300 p-1.5 text-right">{formatQuantity(item.total_qty ?? 0)}</td>
                    <td className="border border-gray-300 p-1.5 text-left">{uomVal || "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ComponentToPrint;
